<script setup>
import { Link, usePage } from '@inertiajs/vue3';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { transs } from '../../lib/locale';
import { trackEvent } from '../../lib/analytics';
import { PhPlanet } from '@phosphor-icons/vue';
import { PhArrowArcLeft, PhArrowLeft, PhArrowRight, PhWhatsappLogo } from '@phosphor-icons/vue';
import { computed } from 'vue';
import { animationCard, fadeBottom, fadeIn } from '@/lib/animations';
import { scrollToId } from '@/lib/scroll';

const page = usePage();

const languages = ref({
    'ar': {
        'title': 'ع',
        'code': 'ar',
    },
    'en': {
        'title': 'EN',
        'code': 'en',
    }
})

const mobileNavOpen = ref(false)

const locale = computed(() => page.props.locale)
const scrolled = ref(false);

watch(locale, (newVal) => {
    document.dir = newVal === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('lang', newVal);
})

watch(mobileNavOpen, (newVal) => {
    if (newVal) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = 'auto';
    }
})


const activeSectionId = ref(null);

const updateActiveSection = () => {
    if (!isHomePage.value) return;
    const offset = 120;
    let current = null;
    for (const link of links) {
        if (!link.id) continue;
        const el = document.getElementById(link.id);
        if (el && el.getBoundingClientRect().top <= offset) {
            current = link.id;
        }
    }
    activeSectionId.value = current;
};

const isLinkActive = (link) => {
    if (!isHomePage.value) return false;
    return link.id ? activeSectionId.value === link.id : !activeSectionId.value;
};

const handleScroll = () => {
    if (window.scrollY > 700) {
        scrolled.value = true;
    } else {
        if (scrolled.value !== false) {
            scrolled.value = false;
        }
    }

    updateActiveSection();
};


onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
});

onMounted(() => {
    document.dir = locale.value === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('lang', locale.value);
    window.addEventListener('scroll', handleScroll);
    updateActiveSection();

})

const animation_list = ref([])
const ac_list = ref([])

watch(animation_list, (lis) => {
    animationCard(lis, 0.3, .3, null)
})

watch(mobileNavOpen, (lis) => {
    if (lis) {
        animation_list.value.forEach((cdx, index) => {
            fadeBottom(cdx, .3 + (index * .2), .4, null)
        })

        ac_list.value.forEach((cdx, index) => {
            fadeIn(cdx, 1.5 + (index * .2), .4)
        })
    } else {
        animation_list.value = []
        ac_list.value = []
    }
})


const changeLocale = (code = null) => {
    //locale.value = code || 'ar';
    // document.dir = locale.value === 'ar' ? 'rtl' : 'ltr';
}

function switchLanguage(lang) {
    const currentPath = window.location.pathname;
    var pathWithoutLang = currentPath.replace(/^\/(en|ar)(\/|$)/, '/');

    pathWithoutLang = pathWithoutLang === '/' ? '' : pathWithoutLang;

    if (lang !== 'ar') {
        return '/en' + pathWithoutLang;
    } else if (lang === 'ar') {
        return pathWithoutLang || '/';
    }
}


const links = [
    { title: 'home', id: null },
    { title: 'about', id: 'about' },
    { title: 'services', id: 'services' },
    { title: 'stats', id: 'stats' },
    { title: 'partners', id: 'partners' },
]

const isHomePage = computed(() => /^\/(en\/?)?$/.test((page.url || '/').split('?')[0].split('#')[0]))

const homeUrl = computed(() => locale.value === 'ar' ? '/' : '/en')

const linkHref = (link) => {
    if (isHomePage.value) return link.id ? ('#' + link.id) : '#'
    return homeUrl.value + (link.id ? ('#' + link.id) : '')
}

const handleNavClick = (link, event) => {
    if (isHomePage.value) {
        event.preventDefault()
    }
    mobileNavOpen.value = false
    if (isHomePage.value) {
        nextTick(() => scrollToId(link.id))
    }
}




</script>

<template>

    <section>
        <!-- Desktop -->
        <div id="site-navbar" :class="{ 'shadow-lg': scrolled }"
            class="bg-white fixed top-0 inset-x-0 z-[800] w-full transition-all ease-in-out duration-300">
            <div class="flex items-center justify-between max-w-7xl mx-auto px-4 md:px-14 py-2.5">

                <!-- Side 1: Logo -->
                <div class="w-auto">
                    <Link :href="locale === 'ar' ? '/' : ('/' + locale)">
                        <img class="h-12 md:h-14 revert !w-auto" :src="$page?.props?.site?.logo || ''"
                            :alt="$page?.props?.site?.name" width="140" height="56" loading="lazy" />
                    </Link>
                </div>

                <!-- Side 2: Links + Language + Mobile toggle -->
                <div class="flex items-center gap-2 md:gap-3">
                    <ul class="hidden lg:flex items-center gap-0.5">
                        <template v-for="link in links" :key="link.title">
                            <li class="font-arabic text-nav rounded-xl transition-colors"
                                :class="isLinkActive(link) ? 'text-brand' : 'text-zinc-500 hover:text-brand'">
                                <component :is="isHomePage ? 'a' : Link"
                                    class="py-2.5 px-4 block w-full h-full rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1"
                                    :href="linkHref(link)"
                                    @click="handleNavClick(link, $event)"
                                    :title="transs.get(link.title, locale)"
                                    :aria-label="transs.get(link.title, locale)"
                                    :aria-current="isLinkActive(link) ? 'true' : undefined">
                                    {{ transs.get(link.title, locale) }}
                                </component>
                            </li>
                        </template>
                    </ul>

                    <div class="hidden lg:flex items-center gap-2">
                        <template v-for="language in languages" :key="language.code">
                            <Link
                                class="flex h-11 w-11 items-center justify-center bg-zinc-100 hover:bg-brand rounded-xl shadow-sm transition-colors"
                                @click="changeLocale(language.code)" :href="switchLanguage(language.code)"
                                :title="language.title" :aria-label="language.title"
                                v-if="language.code !== $page.props.locale">
                                <span class="text-md font-medium hidden sm:block text-zinc-900 hover:text-white"
                                    :lang="language.code">
                                    <span>{{ language.title }}</span>
                                </span>
                            </Link>
                        </template>
                    </div>

                    <div class="lg:hidden flex gap-3 items-center">
                        <template v-for="language in languages" :key="language.code">
                            <Link
                                class="bg-zinc-100 hover:bg-brand rounded-xl w-11 h-11 flex items-center justify-center transition-colors"
                                @click="changeLocale(language.code)" :href="switchLanguage(language.code)"
                                :title="language.title" :aria-label="language.title"
                                v-if="language.code !== $page.props.locale">
                                <span class="text-md font-medium sm:block !text-zinc-900 hover:!text-white"
                                    :lang="language.code">
                                    <span>{{ language.title }}</span>
                                </span>
                            </Link>
                        </template>
                        <button @click="mobileNavOpen = !mobileNavOpen" type="button" class=" outline-none"
                            :title="transs.get('toggle_menu', locale)"
                            :aria-label="transs.get('toggle_menu', locale)" :aria-expanded="mobileNavOpen">
                            <div
                                class="bg-zinc-100 hover:bg-brand rounded-xl w-11 h-11 flex items-center justify-center transition-colors group">
                                <PhList v-if="!mobileNavOpen" class="text-brand group-hover:text-white" :size="30"
                                    weight="bold" />
                                <PhX v-else class="text-brand group-hover:text-white" :size="30"
                                    weight="bold" />
                            </div>
                        </button>
                    </div>
                </div>

            </div>
        </div>

        <!-- Mobile -->

        <div :class="{ 'hidden': !mobileNavOpen }"
            class="block lg:hidden w-full min-h-[100vh] fixed top-0 start-0 end-0 bottom-0 z-[950] bg-white">

            <div class="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
                <div class="absolute -top-24 -end-24 w-72 h-72 rounded-full bg-brand-light/40 blur-3xl"></div>
                <div class="absolute -bottom-32 -start-16 w-72 h-72 rounded-full bg-brand/10 blur-3xl"></div>
            </div>

            <div class="w-full relative h-[100vh] flex flex-col">
                <div class="flex items-center justify-between p-4 border-b border-zinc-100">
                    <Link :href="locale === 'ar' ? '/' : ('/' + locale)" @click="mobileNavOpen = false">
                        <img class="h-10 revert" :src="$page?.props?.site?.logo || ''"
                            :alt="$page?.props?.site?.name" width="100" height="40" loading="lazy" />
                    </Link>

                    <div class="flex items-center gap-1 bg-zinc-50 border border-zinc-100 rounded-2xl p-1.5">
                        <template v-for="language in languages" :key="language.code">
                            <Link
                                class="h-9 w-9 flex items-center justify-center rounded-xl text-sm font-medium text-zinc-600 hover:bg-brand hover:text-white transition-colors"
                                @click="changeLocale(language.code)" :href="switchLanguage(language.code)"
                                :title="language.title" :aria-label="language.title" :lang="language.code"
                                v-if="language.code !== $page.props.locale">
                                {{ language.title }}
                            </Link>
                        </template>
                        <span class="w-px h-5 bg-zinc-200"></span>
                        <button @click="mobileNavOpen = false" type="button" class="outline-none"
                            :title="transs.get('close_menu', locale)"
                            :aria-label="transs.get('close_menu', locale)">
                            <div
                                class="h-9 w-9 flex items-center justify-center rounded-xl bg-zinc-900 hover:bg-brand transition-colors">
                                <PhX class="text-white" :size="18" weight="bold" />
                            </div>
                        </button>
                    </div>
                </div>

                <nav class="flex-1 overflow-y-auto px-5 py-6">
                    <ul class="flex flex-col divide-y divide-zinc-100">
                        <template v-for="(link, index) in links" :key="link.title">
                            <li ref="animation_list">
                                <component :is="isHomePage ? 'a' : Link"
                                    @click="handleNavClick(link, $event)"
                                    class="group flex items-center gap-4 py-4"
                                    :href="linkHref(link)"
                                    :title="transs.get(link.title, locale)"
                                    :aria-label="transs.get(link.title, locale)"
                                    :aria-current="isLinkActive(link) ? 'true' : undefined">

                                    <span class="text-xs font-semibold w-7 h-7 shrink-0 rounded-lg flex items-center justify-center transition-colors"
                                        :class="isLinkActive(link) ? 'bg-brand text-white' : 'bg-zinc-100 text-zinc-400 group-hover:bg-brand-light/60 group-hover:text-brand'">
                                        {{ String(index + 1).padStart(2, '0') }}
                                    </span>

                                    <span class="flex-1 text-2xl font-heading font-medium transition-colors"
                                        :class="isLinkActive(link) ? 'text-brand' : 'text-zinc-800 group-hover:text-brand'">
                                        {{ transs.get(link.title, locale) }}
                                    </span>

                                    <span
                                        class="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-zinc-50 text-brand opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                                        :class="locale == 'ar' ? 'translate-x-2' : '-translate-x-2'">
                                        <PhCaretLeft v-if="locale == 'ar'" :size="16" weight="bold" />
                                        <PhCaretRight v-else :size="16" weight="bold" />
                                    </span>
                                </component>
                            </li>
                        </template>
                    </ul>
                </nav>

                <!-- Footer Navbar Mobile Size -->

                <div class="border-t border-zinc-100 bg-zinc-50/60 px-5 py-5">

                    <p class="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 px-1">
                        {{ transs.get('contact_title', locale) }}
                    </p>

                    <div class="flex flex-col gap-0.5 mb-4">
                        <a v-if="$page?.props?.site?.phone" :href="'tel:' + $page?.props?.site?.phone"
                            :ref="el => el && ac_list.push(el)"
                            class="flex items-center gap-3 py-2 px-2 rounded-xl hover:bg-white transition-colors group">
                            <span
                                class="w-10 h-10 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:bg-brand transition-colors">
                                <PhPhone class="text-brand group-hover:text-white transition-colors" :size="18"
                                    weight="duotone" />
                            </span>
                            <span class="text-sm text-zinc-700" dir="ltr">{{ $page?.props?.site?.phone }}</span>
                        </a>

                        <a v-if="$page?.props?.site?.email" :href="'mailto:' + $page?.props?.site?.email"
                            :ref="el => el && ac_list.push(el)"
                            class="flex items-center gap-3 py-2 px-2 rounded-xl hover:bg-white transition-colors group">
                            <span
                                class="w-10 h-10 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:bg-brand transition-colors">
                                <PhAt class="text-brand group-hover:text-white transition-colors" :size="18"
                                    weight="duotone" />
                            </span>
                            <span class="text-sm text-zinc-700 truncate">{{ $page?.props?.site?.email }}</span>
                        </a>

                        <a v-if="$page?.props?.site?.address_map_link" :href="$page?.props?.site?.address_map_link"
                            target="_blank" :ref="el => el && ac_list.push(el)"
                            class="flex items-center gap-3 py-2 px-2 rounded-xl hover:bg-white transition-colors group">
                            <span
                                class="w-10 h-10 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:bg-brand transition-colors">
                                <PhMapPin class="text-brand group-hover:text-white transition-colors" :size="18"
                                    weight="duotone" />
                            </span>
                            <span class="text-sm text-zinc-700">{{ transs.get('our_location', locale) }}</span>
                        </a>
                    </div>

                    <div class="flex items-center gap-2 flex-wrap px-1">
                        <span class="text-xs font-semibold uppercase tracking-wider text-zinc-400 me-1">
                            {{ transs.get('follow_us', locale) }}
                        </span>

                        <a v-if="$page?.props?.site?.whatsapp" :href="$page?.props?.site?.whatsapp" target="_blank"
                            @click="trackEvent('whatsapp_click', { location: 'mobile_menu' })"
                            :ref="el => el && ac_list.push(el)"
                            class="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-brand transition-colors group">
                            <PhWhatsappLogo class="text-zinc-600 group-hover:text-white transition-colors"
                                :size="16" weight="duotone" />
                        </a>

                        <a v-if="$page?.props?.site?.tiktok" :href="$page?.props?.site?.tiktok" target="_blank"
                            :ref="el => el && ac_list.push(el)"
                            class="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-brand transition-colors group">
                            <PhTiktokLogo class="text-zinc-600 group-hover:text-white transition-colors" :size="16"
                                weight="duotone" />
                        </a>

                        <a v-if="$page?.props?.site?.instagram" :href="$page?.props?.site?.instagram"
                            target="_blank" :ref="el => el && ac_list.push(el)"
                            class="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-brand transition-colors group">
                            <PhInstagramLogo class="text-zinc-600 group-hover:text-white transition-colors"
                                :size="16" weight="duotone" />
                        </a>

                        <a v-if="$page?.props?.site?.snapchat" :href="$page?.props?.site?.snapchat" target="_blank"
                            :ref="el => el && ac_list.push(el)"
                            class="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-brand transition-colors group">
                            <PhSnapchatLogo class="text-zinc-600 group-hover:text-white transition-colors"
                                :size="16" weight="duotone" />
                        </a>

                        <a v-if="$page?.props?.site?.linkedin" :href="$page?.props?.site?.linkedin" target="_blank"
                            :ref="el => el && ac_list.push(el)"
                            class="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-brand transition-colors group">
                            <PhLinkedinLogo class="text-zinc-600 group-hover:text-white transition-colors"
                                :size="16" weight="duotone" />
                        </a>
                    </div>
                </div>
            </div>


        </div>

    </section>

</template>
